import React, { useState, useRef, useEffect } from 'react';
import { Dialog, Tag, Button } from '@workspace/design-system/src/components';
import type { PlanInquiryResponseModel, PlanInquiryStatus } from '@src/models/enterprise-inquiry';
import { useUpdatePlanInquiry } from '@src/hooks/enterpriseInquiry/queries';
import {
    INQUIRY_STATUSES,
    INQUIRY_MANAGERS,
    getStatusColor,
    getStatusBgColor,
    getStatusTextColor,
    getStatusLabel,
    getManagerLabel,
    BUSINESS_TYPE_LABELS,
    getPaymentMethodLabel,
} from '@src/constants/enterprise-inquiry';

interface EnterpriseInquiryModalProps {
    inquiry: PlanInquiryResponseModel | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (inquiry: PlanInquiryResponseModel) => void;
}

export function EnterpriseInquiryModal({ inquiry, isOpen, onClose, onSave }: EnterpriseInquiryModalProps) {
    const [editedStatus, setEditedStatus] = useState<PlanInquiryStatus | null>(null);
    const [editedAdminId, setEditedAdminId] = useState<string | null | undefined>(null);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [isManagerDropdownOpen, setIsManagerDropdownOpen] = useState(false);
    const [memo, setMemo] = useState('');
    const statusDropdownRef = useRef<HTMLDivElement>(null);
    const managerDropdownRef = useRef<HTMLDivElement>(null);
    const updateMutation = useUpdatePlanInquiry();

    // 모달이 열릴 때 초기화
    React.useEffect(() => {
        if (inquiry && isOpen) {
            setEditedStatus(inquiry.status);
            setEditedAdminId(inquiry.adminId);
            setMemo(inquiry.memo || '');
        }
    }, [inquiry, isOpen]);

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setIsStatusDropdownOpen(false);
            }
            if (managerDropdownRef.current && !managerDropdownRef.current.contains(event.target as Node)) {
                setIsManagerDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (!inquiry) return null;

    const handleStatusChange = (status: PlanInquiryStatus) => {
        setEditedStatus(status);
        setIsStatusDropdownOpen(false);
    };

    const handleManagerChange = (adminId: string | null) => {
        setEditedAdminId(adminId || null);
        setIsManagerDropdownOpen(false);
    };

    const handleSave = async () => {
        if (!inquiry || !editedStatus) return;

        try {
            await updateMutation.mutateAsync({
                inquiryId: inquiry.id,
                data: {
                    status: editedStatus,
                    adminId: editedAdminId || null,
                    memo: memo || null,
                },
            });
            onSave(inquiry);
            onClose();
        } catch (error) {
            console.error('문의 수정 실패:', error);
            // TODO: 에러 토스트 표시
        }
    };

    const hasChanges = () => {
        if (!inquiry || !editedStatus) return false;

        return (
            editedStatus !== inquiry.status ||
            editedAdminId !== inquiry.adminId ||
            memo !== (inquiry.memo || '')
        );
    };

    const getCurrentStatusLabel = () => {
        return editedStatus ? getStatusLabel(editedStatus) : '인입';
    };

    const getCurrentManagerLabel = () => {
        return getManagerLabel(editedAdminId);
    };

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'YYYY-MM-DD TT:TT:TT';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('ko-KR', {
                timeZone: 'Asia/Seoul',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            });
        } catch {
            return dateString;
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog>
            <Dialog.Content className="max-w-[1200px] max-h-[840px] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-10 pt-10 pb-0">
                    <h2 className="text-[20px] font-bold text-[#101010] leading-[26px]">
                        문의 내용
                    </h2>
                    <div className="flex items-center gap-2">
                        <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                            문의 담당자
                        </span>
                        <div className="relative" ref={managerDropdownRef}>
                            <button
                                onClick={() => setIsManagerDropdownOpen(!isManagerDropdownOpen)}
                                className="flex items-center gap-1 cursor-pointer"
                            >
                                <Tag
                                    color="gray"
                                    size="S-16"
                                    text={getCurrentManagerLabel()}
                                />
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 6L8 10L12 6" stroke="#595959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            {isManagerDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-[#F0F0F0] z-10">
                                    {INQUIRY_MANAGERS.map((manager) => (
                                        <button
                                            key={manager.value}
                                            onClick={() => handleManagerChange(manager.value)}
                                            className={`w-full px-3 py-2 text-left text-[14px] hover:bg-[#F5F5F5] flex items-center justify-between ${editedAdminId === manager.value ? 'text-[#3C78F4]' : 'text-[#101010]'
                                                }`}
                                        >
                                            {manager.label}
                                            {editedAdminId === manager.value && (
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <circle cx="8" cy="8" r="7" fill="#3C78F4" stroke="#3C78F4" strokeWidth="1" />
                                                    <path d="M5.5 8L7 9.5L10.5 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-10 py-10">
                    {/* 정보 테이블 */}
                    <div className="border border-[#F0F0F0] rounded-lg overflow-hidden mb-6">
                        {/* 기업/단체명 */}
                        <div className="flex h-10 border-b border-[#F0F0F0]">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">기업 / 단체명</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {inquiry.organizationName}
                                </span>
                            </div>
                        </div>

                        {/* 담당자 & 부서 및 직책 */}
                        <div className="flex h-10 border-b border-[#F0F0F0]">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">담당자</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {inquiry.contactName}
                                </span>
                            </div>
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">부서 및 직책</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {inquiry.jobTitle}
                                </span>
                            </div>
                        </div>

                        {/* 이메일 & 연락처 */}
                        <div className="flex h-10 border-b border-[#F0F0F0]">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">이메일</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {inquiry.email}
                                </span>
                            </div>
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">연락처</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {inquiry.phone}
                                </span>
                            </div>
                        </div>

                        {/* 사용 기간 */}
                        <div className="flex h-10 border-b border-[#F0F0F0]">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">사용 기간</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {inquiry.useMonths}개월
                                </span>
                            </div>
                        </div>

                        {/* 결제 방식 */}
                        <div className="flex h-10 border-b border-[#F0F0F0]">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">결제 방식</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {getPaymentMethodLabel(inquiry.paymentMethod)}
                                </span>
                            </div>
                        </div>

                        {/* 비즈니스 유형 */}
                        <div className="flex h-10 border-b border-[#F0F0F0]">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">비즈니스 유형</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {BUSINESS_TYPE_LABELS[inquiry.businessType] || inquiry.businessType}
                                </span>
                            </div>
                        </div>

                        {/* 통화 가능 시간 */}
                        <div className="flex h-10 border-b border-[#F0F0F0]">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">통화 가능 시간</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                    {inquiry.contactAvailableTime || '미정'}
                                </span>
                            </div>
                        </div>

                        {/* 문의 상태 & 문의 접수 일시 */}
                        <div className="flex h-10">
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">문의 상태</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <div className="relative" ref={statusDropdownRef}>
                                    <button
                                        onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                                        className="flex items-center gap-1 cursor-pointer"
                                    >
                                        <Tag
                                            color={getStatusColor(editedStatus || inquiry.status)}
                                            size="S-16"
                                            text={getCurrentStatusLabel()}
                                        />
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M4 6L8 10L12 6" stroke="#595959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                    {isStatusDropdownOpen && (
                                        <div
                                            className="fixed bg-white rounded-lg shadow-lg border border-[#F0F0F0] z-[9999]"
                                            style={{
                                                top: '300px',
                                                left: '200px',
                                                width: '128px'
                                            }}
                                        >
                                            {INQUIRY_STATUSES.map((status) => (
                                                <button
                                                    key={status.value}
                                                    onClick={() => handleStatusChange(status.value)}
                                                    className={`w-full px-3 py-2 text-left text-[14px] hover:bg-[#F5F5F5] flex items-center justify-between ${editedStatus === status.value ? 'text-[#3C78F4]' : 'text-[#101010]'
                                                        }`}
                                                >
                                                    {status.label}
                                                    {editedStatus === status.value && (
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <circle cx="8" cy="8" r="7" fill="#3C78F4" stroke="#3C78F4" strokeWidth="1" />
                                                            <path d="M5.5 8L7 9.5L10.5 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="w-30 bg-[#F5F5F5] flex items-center px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101010]">문의 접수 일시</span>
                            </div>
                            <div className="flex-1 flex items-center px-4">
                                <span className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">
                                    {formatDate(inquiry.createdAt)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* 메모 영역 */}
                    <div className="bg-[#F5F5F5] rounded-lg p-4">
                        <textarea
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            placeholder="메모를 입력해 주세요"
                            className="w-full bg-transparent border-none outline-none text-[14px]  leading-[20px] tracking-[-0.1px] resize-none min-h-[80px]"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-[#F0F0F0] flex justify-end gap-3">
                    <Button
                        onClick={onClose}
                        styleType="basic"
                    >
                        닫기
                    </Button>
                    <Button
                        onClick={handleSave}
                        styleType="primary"
                        disabled={!hasChanges() || updateMutation.isPending}
                    >
                        {updateMutation.isPending ? '저장 중...' : '저장'}
                    </Button>
                </div>
            </Dialog.Content>
        </Dialog>
    );
}
